import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  createSupportTicket,
  getMySupportTickets,
  getMySupportTicket,
  addSupportTicketMessage,
  getCustomerAgents,
  getApiError,
} from '../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, LifeBuoy, MessageSquarePlus, Send } from 'lucide-react';
import { formatDateTime } from '../lib/utils';

const STATUS_LABELS = {
  open: 'Open',
  in_progress: 'In progress',
  waiting_customer: 'Waiting on you',
  on_hold: 'On hold',
  resolved: 'Resolved',
};

function statusBadgeVariant(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'resolved') return 'secondary';
  if (s === 'waiting_customer') return 'outline';
  if (s === 'on_hold') return 'outline';
  return 'default';
}

export function SupportTicketsPage() {
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [search, setSearch] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const res = await getMySupportTickets({ search: search.trim() || undefined, limit: 100, skip: 0 });
      const list = Array.isArray(res?.data?.tickets) ? res.data.tickets : res?.data?.items || [];
      setTickets(list);
    } catch (e) {
      toast.error(getApiError(e).uiMessage || getApiError(e).detail || 'Could not load support tickets');
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-6 max-w-4xl" data-testid="support-tickets-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-jakarta font-bold text-slate-900 flex items-center gap-2">
            <LifeBuoy className="w-7 h-7 text-teal-600" />
            Support
          </h1>
          <p className="text-slate-500 mt-1">Open a ticket about an agent. Plain text only—no images or attachments.</p>
        </div>
        <Button asChild className="bg-teal-600 hover:bg-teal-700 shrink-0">
          <Link to="/support/new">
            <MessageSquarePlus className="w-4 h-4 mr-2" />
            New ticket
          </Link>
        </Button>
      </div>

      <Card className="border-0 shadow-md">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Your tickets</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              placeholder="Search title, ticket ID, or agent ID…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-md"
            />
            <Button type="button" variant="secondary" onClick={load}>
              Search / refresh
            </Button>
          </div>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : tickets.length === 0 ? (
            <p className="text-sm text-slate-500 py-8 text-center">No tickets yet. Create one if you need help.</p>
          ) : (
            <ul className="divide-y divide-slate-100 rounded-lg border border-slate-100">
              {tickets.map((t) => {
                const id = t.ticket_id || t.id;
                return (
                  <li key={id}>
                    <Link
                      to={`/support/${encodeURIComponent(id)}`}
                      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 py-3 hover:bg-slate-50 transition-colors"
                    >
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 truncate">{t.title || 'Untitled'}</p>
                        <p className="text-xs text-slate-500 truncate">
                          {id} · Agent {t.agent_id || '—'} · Updated {formatDateTime(t.updated_at || t.created_at)}
                        </p>
                      </div>
                      <Badge variant={statusBadgeVariant(t.status)} className="shrink-0 w-fit">
                        {STATUS_LABELS[String(t.status || '').toLowerCase()] || t.status || '—'}
                      </Badge>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

const TITLE_MAX = 200;
const CONTENT_MAX = 8000;

export function SupportNewTicketPage() {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [agentId, setAgentId] = useState('');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await getCustomerAgents({ limit: 200 });
        const list = Array.isArray(res?.data?.agents) ? res.data.agents : [];
        setAgents(list);
        if (list.length > 0) setAgentId(list[0].agent_id);
      } catch {
        setAgents([]);
      }
    })();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agentId) {
      toast.error('Select an agent');
      return;
    }
    if (!title.trim()) {
      toast.error('Enter a short title');
      return;
    }
    if (!content.trim()) {
      toast.error('Describe your issue');
      return;
    }
    setSubmitting(true);
    try {
      const res = await createSupportTicket({
        agent_id: agentId,
        title: title.trim().slice(0, TITLE_MAX),
        content: content.trim().slice(0, CONTENT_MAX),
      });
      const id = res?.data?.ticket?.ticket_id || res?.data?.ticket_id || res?.data?.id;
      if (id) {
        toast.success('Ticket created');
        navigate(`/support/${encodeURIComponent(id)}`);
      } else {
        toast.success('Ticket created');
        navigate('/support');
      }
    } catch (err) {
      toast.error(getApiError(err).uiMessage || getApiError(err).detail || 'Could not create ticket');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="support-new-page">
      <Button variant="ghost" asChild className="-ml-2 text-slate-600">
        <Link to="/support">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to tickets
        </Link>
      </Button>
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle>New support ticket</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1.5">Agent</label>
              <Select value={agentId} onValueChange={setAgentId} disabled={agents.length === 0}>
                <SelectTrigger>
                  <SelectValue placeholder="Select agent" />
                </SelectTrigger>
                <SelectContent>
                  {agents.map((a) => (
                    <SelectItem key={a.agent_id} value={a.agent_id}>
                      {a.name || a.agent_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {agents.length === 0 && (
                <p className="text-xs text-amber-700 mt-1">No agents found. Create an agent first.</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1.5">Title</label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value.slice(0, TITLE_MAX))}
                placeholder="Short summary of the issue"
                maxLength={TITLE_MAX}
              />
              <p className="text-xs text-slate-400 mt-1">{title.length}/{TITLE_MAX}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1.5">Details</label>
              <Textarea
                value={content}
                onChange={(e) => setContent(e.target.value.slice(0, CONTENT_MAX))}
                placeholder="Plain text only. No images or file uploads."
                rows={8}
                className="resize-y min-h-[160px]"
                maxLength={CONTENT_MAX}
              />
              <p className="text-xs text-slate-400 mt-1">{content.length}/{CONTENT_MAX}</p>
            </div>
            <Button type="submit" className="bg-teal-600 hover:bg-teal-700" disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit ticket'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export function SupportTicketDetailPage() {
  const { ticketId } = useParams();
  const [loading, setLoading] = useState(true);
  const [ticket, setTicket] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const res = await getMySupportTicket(ticketId);
      setTicket(res?.data?.ticket || res?.data || null);
    } catch (e) {
      toast.error(getApiError(e).uiMessage || getApiError(e).detail || 'Could not load ticket');
      setTicket(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [ticketId]);

  const handleReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try {
      await addSupportTicketMessage(ticketId, { content: reply.trim().slice(0, CONTENT_MAX) });
      setReply('');
      toast.success('Message sent');
      await load();
    } catch (err) {
      toast.error(getApiError(err).uiMessage || getApiError(err).detail || 'Could not send');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4 max-w-3xl">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="max-w-3xl">
        <Button variant="ghost" asChild>
          <Link to="/support">Back to tickets</Link>
        </Button>
        <p className="text-slate-600 mt-4">Ticket not found.</p>
      </div>
    );
  }

  const messages = Array.isArray(ticket.messages) ? ticket.messages : [];
  const sorted = [...messages].sort(
    (a, b) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime()
  );

  return (
    <div className="space-y-6 max-w-3xl" data-testid="support-ticket-detail-page">
      <Button variant="ghost" asChild className="-ml-2 text-slate-600">
        <Link to="/support">
          <ArrowLeft className="w-4 h-4 mr-2" />
          All tickets
        </Link>
      </Button>

      <Card className="border-0 shadow-md">
        <CardHeader className="space-y-2">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <CardTitle className="text-xl leading-tight">{ticket.title || 'Support ticket'}</CardTitle>
            <Badge variant={statusBadgeVariant(ticket.status)}>
              {STATUS_LABELS[String(ticket.status || '').toLowerCase()] || ticket.status}
            </Badge>
          </div>
          <p className="text-sm text-slate-500">
            Ticket <span className="font-mono text-xs">{ticket.ticket_id || ticketId}</span> · Agent{' '}
            <span className="font-mono text-xs">{ticket.agent_id}</span>
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="rounded-lg border border-slate-100 bg-slate-50/80 p-4 space-y-3">
            <p className="text-xs font-medium uppercase text-slate-500">Original message</p>
            <p className="text-sm text-slate-800 whitespace-pre-wrap break-words">{ticket.content || ticket.body}</p>
          </div>

          <div className="space-y-3">
            <p className="text-sm font-semibold text-slate-800">Conversation</p>
            {sorted.length === 0 ? (
              <p className="text-sm text-slate-500">No replies yet.</p>
            ) : (
              <ul className="space-y-3">
                {sorted.map((m) => {
                  const mid = m.message_id || m.id;
                  const from = String(m.author_role || m.role || '').toLowerCase();
                  const isStaff = from === 'admin' || from === 'staff' || from === 'agent';
                  return (
                    <li
                      key={mid || `${m.created_at}-${from}`}
                      className={`rounded-lg border p-3 text-sm ${
                        isStaff ? 'border-teal-100 bg-teal-50/50' : 'border-slate-100 bg-white'
                      }`}
                    >
                      <div className="flex justify-between gap-2 text-xs text-slate-500 mb-1">
                        <span className="font-medium text-slate-700">
                          {isStaff ? 'Support team' : 'You'}
                        </span>
                        <span>{formatDateTime(m.created_at)}</span>
                      </div>
                      <p className="text-slate-800 whitespace-pre-wrap break-words">{m.content || m.body}</p>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {String(ticket.status || '').toLowerCase() !== 'resolved' ? (
            <form onSubmit={handleReply} className="space-y-2 border-t border-slate-100 pt-4">
              <label className="text-sm font-medium text-slate-700">Add a message</label>
              <Textarea
                value={reply}
                onChange={(e) => setReply(e.target.value.slice(0, CONTENT_MAX))}
                rows={4}
                placeholder="Plain text only"
                maxLength={CONTENT_MAX}
              />
              <Button type="submit" disabled={sending || !reply.trim()} className="bg-teal-600 hover:bg-teal-700">
                <Send className="w-4 h-4 mr-2" />
                {sending ? 'Sending…' : 'Send'}
              </Button>
            </form>
          ) : (
            <p className="text-sm text-slate-500">This ticket is resolved. Open a new ticket if you need more help.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
